import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import {
  getActionOptions,
  getConditionOptions,
} from 'dashboard/helper/automationHelper';
import {
  MESSAGE_CONDITION_VALUES,
  PRIORITY_CONDITION_VALUES,
} from 'dashboard/constants/automation';
import AiAgentsAPI from 'dashboard/api/aiAgents';
import OffersAPI from 'dashboard/api/offers';
import FunnelAPI from 'dashboard/api/funnel';

/**
 * This is a shared composables that holds utilities used to build dropdown and file options
 * @returns {Object} An object containing various automation-related functions and computed properties.
 */
export default function useAutomationValues() {
  const getters = useStoreGetters();
  const { t } = useI18n();
  const agents = useMapGetter('agents/getAgents');
  const campaigns = useMapGetter('campaigns/getAllCampaigns');
  const contacts = useMapGetter('contacts/getContacts');
  const inboxes = useMapGetter('inboxes/getInboxes');
  const labels = useMapGetter('labels/getLabels');
  const teams = useMapGetter('teams/getTeams');
  const slaPolicies = useMapGetter('sla/getSLA');

  // AI Agents for automation dropdown
  const aiAgents = ref([]);

  // Offers for automation dropdown
  const offers = ref([]);

  // Funnels and stages for Kanban automation conditions
  const funnels = ref([]);
  const funnelStages = ref([]);

  // Fetch AI agents on mount
  const fetchAiAgents = async () => {
    try {
      const response = await AiAgentsAPI.getAll();
      aiAgents.value = response.data || [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[useAutomationValues] Failed to fetch AI agents:', error);
      aiAgents.value = [];
    }
  };

  // Fetch offers on mount
  const fetchOffers = async () => {
    try {
      // OffersAPI usa getOffers() não getAll()
      const response = await OffersAPI.getOffers();
      // A resposta pode ser paginada (response.data.payload) ou array direto (response.data)
      const offersData = response.data?.payload || response.data || [];
      // Map offers to the format expected by the dropdown
      offers.value = offersData.map(offer => ({
        id: offer.id,
        name: offer.title || offer.name || `Oferta #${offer.id}`,
      }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[useAutomationValues] Failed to fetch offers:', error);
      offers.value = [];
    }
  };

  // Fetch funnels and derive stages
  const fetchFunnels = async () => {
    try {
      const response = await FunnelAPI.getAll();
      const funnelsData = response.data || [];
      // Map funnels for dropdown (id + name)
      funnels.value = funnelsData.map(f => ({
        id: f.id,
        name: f.name,
      }));
      // Derive unique stages from all funnels
      const stagesSet = new Map();
      funnelsData.forEach(f => {
        if (f.stages && typeof f.stages === 'object') {
          Object.keys(f.stages).forEach(stageName => {
            if (!stagesSet.has(stageName)) {
              stagesSet.set(stageName, {
                id: stageName,
                name: stageName,
              });
            }
          });
        }
      });
      funnelStages.value = Array.from(stagesSet.values());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[useAutomationValues] Failed to fetch funnels:', error);
      funnels.value = [];
      funnelStages.value = [];
    }
  };

  // Automatically fetch AI agents, offers and funnels when composable is used
  onMounted(() => {
    fetchAiAgents();
    fetchOffers();
    fetchFunnels();
  });

  const booleanFilterOptions = computed(() => [
    { id: true, name: t('FILTER.ATTRIBUTE_LABELS.TRUE') },
    { id: false, name: t('FILTER.ATTRIBUTE_LABELS.FALSE') },
  ]);

  const statusFilterItems = computed(() => {
    return {
      open: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
      },
      resolved: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
      },
      pending: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
      },
      snoozed: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
      },
      all: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
      },
    };
  });

  const statusFilterOptions = computed(() => {
    const statusFilters = statusFilterItems.value;
    return [
      ...Object.keys(statusFilters).map(status => ({
        id: status,
        name: statusFilters[status].TEXT,
      })),
      { id: 'all', name: t('CHAT_LIST.FILTER_ALL') },
    ];
  });

  const messageTypeOptions = computed(() =>
    MESSAGE_CONDITION_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.MESSAGE_TYPES.${item.i18nKey}`),
    }))
  );

  const priorityOptions = computed(() =>
    PRIORITY_CONDITION_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.PRIORITY_TYPES.${item.i18nKey}`),
    }))
  );

  /**
   * Adds a translated "None" option to the beginning of a list
   * @param {Array} list - The list to add "None" to
   * @returns {Array} A new array with "None" option at the beginning
   */
  const addNoneToList = list => [
    {
      id: 'nil',
      name: t('AUTOMATION.NONE_OPTION') || 'None',
    },
    ...(list || []),
  ];

  /**
   * Gets the condition dropdown values for a given type.
   * @param {string} type - The type of condition.
   * @returns {Array} An array of condition dropdown values.
   */
  const getConditionDropdownValues = type => {
    return getConditionOptions({
      agents: agents.value,
      booleanFilterOptions: booleanFilterOptions.value,
      campaigns: campaigns.value,
      contacts: contacts.value,
      customAttributes: getters['attributes/getAttributes'].value,
      inboxes: inboxes.value,
      labels: labels.value,
      statusFilterOptions: statusFilterOptions.value,
      priorityOptions: priorityOptions.value,
      messageTypeOptions: messageTypeOptions.value,
      teams: teams.value,
      languages,
      countries,
      funnels: funnels.value,
      funnelStages: funnelStages.value,
      offers: offers.value,
      type,
    });
  };

  /**
   * Gets the action dropdown values for a given type.
   * @param {string} type - The type of action.
   * @returns {Array} An array of action dropdown values.
   */
  const getActionDropdownValues = type => {
    return getActionOptions({
      agents: agents.value,
      labels: labels.value,
      teams: teams.value,
      slaPolicies: slaPolicies.value,
      aiAgents: aiAgents.value,
      offers: offers.value,
      inboxes: inboxes.value,
      languages,
      type,
      addNoneToListFn: addNoneToList,
      priorityOptions: priorityOptions.value,
    });
  };

  return {
    booleanFilterOptions,
    statusFilterItems,
    statusFilterOptions,
    priorityOptions,
    messageTypeOptions,
    getConditionDropdownValues,
    getActionDropdownValues,
    agents,
    campaigns,
    contacts,
    inboxes,
    labels,
    teams,
    slaPolicies,
    aiAgents,
    fetchAiAgents,
    offers,
    fetchOffers,
    funnels,
    funnelStages,
    fetchFunnels,
  };
}

